import { Box, Typography } from "@mui/material";
import React from "react";
import sanitizeHtml from "@/utils/sanitize-html";
import { BasicInfo } from "@/types/dto/common/basic-info";

interface TextBlocksProps {
  textBlocks?: BasicInfo[];
  moduleRankingPosition?: string | number;
  parentContainerType?: string;
}

const renderHeaderWithBreak = (text: string) => {
  const words = text.split(" ");
  if (words.length < 2) return text;
  const breakAt = words.length === 2 ? 1 : 2;
  const firstPart = words.slice(0, breakAt).join(" ");
  const secondPart = words.slice(breakAt).join(" ");
  return (
    <>
      {`${firstPart} `}
      <Box component="br" sx={{ display: { xs: "none", md: "block" } }} />
      {secondPart}
    </>
  );
};

const TextBlocks: React.FC<TextBlocksProps> = ({
  textBlocks = [],
  moduleRankingPosition = "2",
  parentContainerType = "text block"
}) => {
  const validBlocks = textBlocks.filter((block) => block?.headerText || block?.copyText);

  if (!validBlocks.length) {
    return null;
  }

  return (
    <Box
      component="section"
      data-track="visibility"
      data-testid="travel-styles-text-blocks"
      data-parent-container-type={parentContainerType}
      data-module-name="Travel Styles Text Blocks"
      data-ranking-algorithm="manual"
      data-module-ranking-position={String(moduleRankingPosition)}
      className="pattern-bg"
    >
      <Box
        className="full-width-container"
        sx={{
          width: "100%",
          display: "flex",
          p: { xs: "3.2rem 2.4rem", sm: "3.2rem 3.2rem", lg: "6rem 10rem" },
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "flex-start",
          alignItems: "stretch",
          gap: "24px",
          position: "relative",
          zIndex: 2
        }}
      >
        {validBlocks.map((block, index) => (
          <Box
            key={`${block.headerText || "text-block"}-${block.copyText || "copy"}`}
            sx={{
              display: "flex",
              minWidth: 270,
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              textAlign: { xs: "center", md: "left" },
              gap: 1
            }}
          >
            {block.headerText && (
              <Typography
                data-testid={`about-style-header-${index}`}
                variant="h3"
                component="h2"
                sx={{
                  color: "secondary.main",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  fontSize: { xs: "2.6rem", md: "2.8rem", lg: "3.2rem" },
                  lineHeight: 1.2
                }}
              >
                {renderHeaderWithBreak(block.headerText)}
              </Typography>
            )}

            {block.copyText && (
              <Typography
                data-testid={`about-style-copy-${index}`}
                component="div"
                sx={{
                  color: "text.primary",
                  fontSize: { xs: "1.6rem", md: "1.8rem" },
                  lineHeight: 1.4,
                  "& p": { margin: "0 !important" }
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.copyText) }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TextBlocks;
